import React, { useEffect, useState } from "react";
import { Button, Form, Input, Table, Tag } from "antd";
import PageHeader from "../../components/header/PageHeader";
import { useAuth } from "../../store/AuthContext";
import { useToast } from "../../components/toast";
import api from "../../api";

const ACTION_MAP = {
  LOGIN: { text: "LOGIN", color: "green" },
  LOGOUT: { text: "LOGOUT", color: "default" },
  CREATE: { text: "CREATE", color: "blue" },
  UPDATE: { text: "UPDATE", color: "orange" },
  DELETE: { text: "DELETE", color: "red" },
};

const auditColumns = [
  {
    title: "Үйлдэл",
    dataIndex: "action",
    key: "action",
    render: (v) => {
      const a = ACTION_MAP[v] || { text: v, color: "default" };
      return <Tag color={a.color}>{a.text}</Tag>;
    },
  },
  {
    title: "Модель",
    dataIndex: "model",
    key: "model",
    render: (v) => v || "-",
  },
  {
    title: "IP",
    dataIndex: "ip",
    key: "ip",
    render: (v) => v || "-",
  },
  {
    title: "Огноо",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 170,
    render: (v) => new Date(v).toLocaleString("mn-MN"),
  },
];

export default function Settings() {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const { user, setUser } = useAuth();
  const toast = useToast();

  const [auditLogs, setAuditLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // ─── Profile form-д user утга тавих ──────────────
  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        name: user.name,
        email: user.email,
      });
    }
  }, [user]);

  // ─── Audit logs авах ──────────────────────────────
  useEffect(() => {
    const fetchLogs = async () => {
      setLogsLoading(true);
      const res = await api.my_audit_logs();
      if (res?.status === 200) {
        setAuditLogs(res.data);
      }
      setLogsLoading(false);
    };
    fetchLogs();
  }, []);

  // ─── Profile хадгалах ─────────────────────────────
  const saveProfile = async () => {
    try {
      const values = await profileForm.validateFields();
      setProfileLoading(true);

      const res = await api.update_profile({
        name: values.name.trim(),
        email: values.email.trim(),
      });

      if (res?.status === 200) {
        setUser(res.data); // AuthContext шинэчлэх
        toast("Профайл хадгалагдлаа", "success");
      } else {
        toast(res?.data?.message || "Хадгалахад алдаа гарлаа", "error");
      }
    } catch {
      // validation
    } finally {
      setProfileLoading(false);
    }
  };

  // ─── Нууц үг солих ───────────────────────────────
  const changePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setPasswordLoading(true);

      const res = await api.change_password({
        current_password: values.current_password,
        new_password: values.new_password,
      });

      if (res?.status === 200) {
        toast("Нууц үг амжилттай солигдлоо", "success");
        passwordForm.resetFields();
      } else {
        toast(res?.data?.message || "Алдаа гарлаа", "error");
      }
    } catch {
      // validation
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <section className="settings-section">
      <PageHeader title="Тохиргоо" sub="Хувийн мэдээлэл болон аюулгүй байдал" />

      <div className="row g-4">
        {/* 🧑 Profile */}
        <div className="col-lg-6">
          <div className="settings-card">
            <div className="card-title">Хувийн тохиргоо</div>
            <Form layout="vertical" form={profileForm}>
              <Form.Item
                label="Нэр"
                name="name"
                rules={[{ required: true, message: "Нэр оруулна уу" }]}
              >
                <Input placeholder="Таны нэр" />
              </Form.Item>

              <Form.Item
                label="Имэйл"
                name="email"
                rules={[
                  { required: true, message: "Имэйл оруулна уу" },
                  { type: "email", message: "Имэйл буруу форматтай" },
                ]}
              >
                <Input placeholder="example@mail.com" />
              </Form.Item>

              <Button
                type="primary"
                loading={profileLoading}
                onClick={saveProfile}
              >
                Хадгалах
              </Button>
            </Form>
          </div>
        </div>

        {/* 🔐 Password */}
        <div className="col-lg-6">
          <div className="settings-card">
            <div className="card-title">Нууц үг солих</div>
            <Form layout="vertical" form={passwordForm}>
              <Form.Item
                label="Одоогийн нууц үг"
                name="current_password"
                rules={[
                  { required: true, message: "Одоогийн нууц үг оруулна уу" },
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                label="Шинэ нууц үг"
                name="new_password"
                rules={[
                  { required: true, message: "Шинэ нууц үг оруулна уу" },
                  { min: 8, message: "Хамгийн багадаа 8 тэмдэгт" },
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                label="Шинэ нууц үг (давтах)"
                name="confirm_password"
                dependencies={["new_password"]}
                rules={[
                  { required: true, message: "Давтан оруулна уу" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("new_password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Нууц үг таарахгүй байна"),
                      );
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Button
                danger
                type="primary"
                loading={passwordLoading}
                onClick={changePassword}
              >
                Нууц үг солих
              </Button>
            </Form>
          </div>
        </div>

        {/* 📋 Audit log */}
        <div className="col-lg-12">
          <div className="settings-card">
            <div className="card-head">
              <div>
                <div className="card-title">Аюулгүй байдал</div>
                <div className="card-sub">
                  Сүүлийн үйлдлүүдийн бүртгэл (audit log)
                </div>
              </div>
            </div>

            <Table
              rowKey="id"
              loading={{ spinning: logsLoading, size: "small" }}
              columns={auditColumns}
              dataSource={auditLogs}
              pagination={false}
              size="middle"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
